import React, { createContext, useContext, useEffect, useState } from "react";
import {
  clearStoredSession,
  getStoredAccessToken,
  logout,
  refreshSession,
  storeSession,
} from "helpers/apiClient";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authAccessToken, setAuthAccessToken] = useState(null);
  const [authAccessTokenExpiration, setAuthAccessTokenExpiration] = useState(null);
  const [authUserId, setAuthUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const applySession = (session) => {
    if (!session?.accessToken) return;
    setAuthAccessToken(session.accessToken);
    setAuthAccessTokenExpiration(session.accessTokenExpiresAt || null);
    if (session.userId !== undefined) setAuthUserId(session.userId);
    setIsAuthenticated(true);
  };

  const clearSession = () => {
    setAuthAccessToken(null);
    setAuthAccessTokenExpiration(null);
    setAuthUserId(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    let mounted = true;
    const restore = async () => {
      try {
        // The refresh cookie is the durable browser session. Rotating it here also
        // restores sessions opened in another tab, where sessionStorage is empty.
        const session = await refreshSession();
        if (mounted) applySession(session);
      } catch (_) {
        if (mounted) clearSession();
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    const onSession = (event) => mounted && applySession(event.detail);
    const onSessionExpired = () => mounted && clearSession();
    window.addEventListener("hortatech:session", onSession);
    window.addEventListener("hortatech:session-expired", onSessionExpired);
    restore();

    return () => {
      mounted = false;
      window.removeEventListener("hortatech:session", onSession);
      window.removeEventListener("hortatech:session-expired", onSessionExpired);
    };
  }, []);

  const handleLogin = (session) => {
    if (session?.accessToken) storeSession(session);
    const accessToken = session?.accessToken || getStoredAccessToken();
    if (accessToken) {
      applySession(
        session || {
          accessToken,
          accessTokenExpiresAt: sessionStorage.getItem("accessTokenExpiry"),
          userId: sessionStorage.getItem("userId"),
        }
      );
    } else {
      // Preserves the existing local guest-preview behavior; it never receives
      // a Go API token and therefore cannot call protected Go endpoints.
      setIsAuthenticated(true);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (_) {
      // A local logout must still succeed if the network/session has expired.
      clearStoredSession();
    } finally {
      clearSession();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        handleLogin,
        handleLogout,
        authAccessToken,
        setAuthAccessToken,
        authAccessTokenExpiration,
        setAuthAccessTokenExpiration,
        authUserId,
        setAuthUserId,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider };
export const useAuth = () => useContext(AuthContext);
